import type { FC } from 'react';
import { useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import AddressAutocompleteInput from 'components/atoms/Form/AddressAutocompleteInput';
import { Select } from 'components/atoms/Form/Select';
import { Button } from 'components/atoms/Button';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import type { BankingSignupBornDetailsFormValues } from '../constants/onboarding.types';
import type { ActivationStepProps } from '../../ActivationFlow/types';
import { BornDetailsSchema } from '../../utils/validation';
import useCountryAlpha3Options from 'hooks/shared/useCountryAlpha3Options';

interface BornDetailsStepProps extends ActivationStepProps {
  defaultValues?: {
    birthCity?: string;
    birthPostalCode?: string;
    birthCountry?: string;
    nationality?: string;
  };
  onConfirm: (values: BankingSignupBornDetailsFormValues) => void;
  isConfirming?: boolean;
}

const BornDetailsStep: FC<BornDetailsStepProps> = ({
  onConfirm,
  defaultValues,
  isConfirming,
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  showBackButton,
  onBack,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const {
    options: countryOptions,
    alpha3ToAlpha2,
    alpha2ToAlpha3,
  } = useCountryAlpha3Options();

  const methods = useForm({
    defaultValues: defaultValues ?? {
      birthCity: '',
      birthPostalCode: '',
      birthCountry: '',
      nationality: '',
    },
    resolver: joiResolver(BornDetailsSchema(), {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const setAndValidate = useCallback(
    (
      name: 'birthPostalCode' | 'birthCity' | 'birthCountry' | 'nationality',
      value: string,
    ) =>
      methods.setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
      }),
    [methods],
  );

  const [birthCountryWatched, birthCityWatched] = methods.watch([
    'birthCountry',
    'birthCity',
  ]);

  const birthPostcodeRegionRestriction = birthCountryWatched?.trim()
    ? alpha3ToAlpha2(birthCountryWatched)
    : undefined;

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton={showBackButton}
      onBack={onBack}
    >
      <FormProvider {...methods}>
        <form
          className="w-[80%] mx-auto"
          onSubmit={methods.handleSubmit(() =>
            // todo: consider remove any type
            onConfirm(methods.getValues() as any),
          )}
        >
          <div className="flex space-x-3">
            <FieldConnector
              className="mt-3 flex-1"
              name="birthPostalCode"
              label={t('birthplace_postcode')}
            >
              {({ field }) => (
                <AddressAutocompleteInput
                  {...field}
                  placeholder={t('settings.companyDetails.postalCode')}
                  includedPrimaryTypes={['postal_code']}
                  countryRestrictions={
                    birthPostcodeRegionRestriction
                      ? [birthPostcodeRegionRestriction]
                      : []
                  }
                  locationHint={[birthCityWatched]}
                  onAddressSelect={(address) => {
                    const alpha3 = alpha2ToAlpha3(address.country);
                    const zip = address.zip || field.value;
                    if (zip) setAndValidate('birthPostalCode', zip);
                    if (address.city) setAndValidate('birthCity', address.city);
                    if (alpha3) {
                      setAndValidate('birthCountry', alpha3);
                      setAndValidate('nationality', alpha3);
                    }
                  }}
                />
              )}
            </FieldConnector>
            <FieldConnector
              className="mt-3 flex-1"
              name="birthCity"
              label={t('birth_city')}
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  placeholder={t('settings.companyDetails.city')}
                />
              )}
            </FieldConnector>
          </div>

          <FieldConnector
            className="mt-3"
            name="birthCountry"
            label={t('birth_country')}
          >
            {({ field, name }) => (
              <Select
                {...field}
                name={name}
                placeholder={t('birth_country')}
                options={countryOptions}
                isSearchable
                value={field.value || undefined}
                // todo: consider remove any type
                onChange={({ value }: any) => field.onChange(value)}
              />
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name="nationality"
            label={t('nationality')}
          >
            {({ field, name }) => (
              <Select
                {...field}
                name={name}
                placeholder={t('nationality')}
                options={countryOptions}
                isSearchable
                value={field.value || undefined}
                // todo: consider remove any type
                onChange={({ value }: any) => field.onChange(value)}
              />
            )}
          </FieldConnector>

          <div className="text-center">
            <Button
              type="submit"
              color="bank"
              className="my-6 mt-12 w-full capitalize"
              loading={isConfirming}
              disabled={isUpdatingStep}
            >
              {t('continue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </OnboardingStepWrapper>
  );
};

export default BornDetailsStep;
